import { WBLocationCompletionV2 } from '@admiin-com/ds-amplify-web';
import { AddressResponse } from '@admiin-com/ds-amplify-web';
import React, { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  mapStateCode,
  mapCountryCode,
  normalizeCountryName,
} from '../../../helpers/entities';
import { getNestedError } from '../../../helpers/string';
import { WBFlex, WBLink } from '@admiin-com/ds-web';
import { AddressManualFields } from './AddressManualFields';

export function AddressForm({
  label,
  name,
  placeholder,
  onlyAustralia = true,
  optional = false,
  disableManual = true,
  isPostal = false,
}: {
  label: string;
  name: string;
  placeholder: string;
  onlyAustralia?: boolean;
  optional?: boolean;
  disableManual?: boolean;
  isPostal?: boolean;
}) {
  const {
    control,
    formState: { errors },
    setValue,
    clearErrors,
  } = useFormContext();

  const { t } = useTranslation();

  const [isManual, setIsManual] = React.useState(false);

  const inputs = useMemo(
    () => ({
      label: label,
      name: `${name}.fullAddress` as const,
      type: 'text',
      placeholder: placeholder,
      defaultValue: '',
      rules: {
        required:
          optional || isManual
            ? false
            : t('addressRequired', { ns: 'contacts' }),
        pattern: {
          value: /.+/,
          message: t('addressPattern', { ns: 'contacts' }),
        },
      },
    }),
    [t, optional, label, name, placeholder, isManual]
  );
  return (
    <WBFlex flexDirection={['column']}>
      {!isManual && (
        <Controller
          control={control}
          name={inputs.name}
          rules={inputs.rules}
          render={({ field }) => (
            <WBLocationCompletionV2
              {...field}
              onlyAustralia={onlyAustralia}
              label={inputs.label}
              type={inputs.type}
              placeholder={inputs.placeholder}
              error={!!getNestedError(errors, field.name)}
              helperText={
                (getNestedError(errors, field.name)?.message as string) ?? ''
              }
              onChange={(address: AddressResponse) => {
                field.onChange(address.label);
                console.log(address);
                const normalizedCountry = normalizeCountryName(address.country);
                setValue(`${name}.addressId`, address.id);
                setValue(
                  `${name}.address1`,
                  [address.addressNumber, address.street]
                    .filter(Boolean)
                    .join(' ')
                );
                setValue(`${name}.address2`, address.unitNumber);
                setValue(`${name}.postalCode`, address.postalCode);
                setValue(`${name}.city`, address.neighborhood);
                setValue(`${name}.state`, address.region);
                setValue(`${name}.stateCode`, mapStateCode(address.region));
                setValue(`${name}.country`, normalizedCountry);
                setValue(
                  `${name}.countryCode`,
                  mapCountryCode(normalizedCountry)
                );
              }}
              margin="dense"
            />
          )}
        />
      )}

      {!disableManual && !isManual && (
        <WBLink
          variant="body2"
          mt={2}
          underline="always"
          onClick={() => {
            setIsManual(true);
            clearErrors(inputs.name);
            setValue(inputs.name, '');
          }}
        >
          {t('enterAddressManually', { ns: 'onboarding' })}
        </WBLink>
      )}
      {isManual && <AddressManualFields name={name} isPostal={isPostal} />}
    </WBFlex>
  );
}

export default AddressForm;
